import PropTypes from 'prop-types'
import React, { Component } from 'react'

import { isEmpty } from '../../utilities/GeneralUtils'
import ABaseLink from '../link/ABaseLink'
import HTag, { HEADER_TYPES } from '../text/HTag'
import APanel from './APanel'

/** Custom Active expandable panel built off of react panel -  https://react-bootstrap.github.io/components.html#panels.

Different then AExpandPanel in the following wayys:
- Header can be any content (not just a label)
- Has a footer (which can also be any content)
- Default styling of blue (cerulean) background and white text in Header
- Default up and down arrows for expand and collapse iconClass
- Currently only supports blue background colour theme, but if a new colour is supplied, just update containers.less to incorporate the new colour scheme

@example ../../../../docs/external/container/AExpandPanelCustom.md */

export const THEME_CERULEAN = 'cerulean'
export const THEME_WHITE = 'white'
export const THEME_YELLOW = 'yellow'
export const THEME_GREEN = 'green'
export const COLOUR_THEMES = [
  THEME_CERULEAN,
  THEME_WHITE,
  THEME_YELLOW,
  THEME_GREEN,
]

class AExpandPanelCustom extends Component {
  constructor(...args) {
    super(...args)
    const initialOpenState = this.props.defaultOpen
    this.state = { open: initialOpenState }
  }

  componentDidUpdate(prevProps) {
    const { defaultOpen } = this.props

    if (defaultOpen !== prevProps.defaultOpen) {
      this.setState({
        open: defaultOpen,
      })
    }
  }

  toggle() {
    const { onToggle } = this.props
    const newState = !this.state.open
    if (onToggle) {
      onToggle(newState)
    }
    this.setState({ open: newState })
  }

  render() {
    const {
      headerOpen,
      headerClose = headerOpen,
      headerOpenLabel,
      headerCloseLabel = headerOpenLabel,
      headerLabelHeaderType = null,
      children,
      footer,
      openIconClass = 'fa-chevron-down',
      closeIconClass = 'fa-chevron-up',
      colourTheme = THEME_CERULEAN,
      hasBorder = true,
      hasMargin = true,
      hasPadding = true,
      cmpId = null,
    } = this.props

    let colourClass = ''
    let customClass = ''
    let footerContent = ''
    let headerContent = ''
    let headerLabel = ''
    let iconClass = ''

    if (isEmpty(headerOpen)) {
      iconClass = this.state.open ? closeIconClass : openIconClass
      if (this.state.open) {
        headerLabel = headerCloseLabel
      } else {
        headerLabel = headerOpenLabel
      }
      const content = (
        <span className={`fa ${iconClass}`}>
          {' '}
          <span className="expand-panel-custom__header-label">
            {headerLabel}
          </span>
        </span>
      )
      let wrappedContent = content
      if (headerLabelHeaderType !== null) {
        wrappedContent = <HTag type={headerLabelHeaderType}>{content}</HTag>
      }
      headerContent = (
        <ABaseLink
          onClick={() => {
            this.toggle()
          }}
          cmpExpanded={this.state.open}
          cmpControlsId={cmpId}
        >
          {wrappedContent}
        </ABaseLink>
      )
      colourClass = colourTheme
    } else if(!this.props.onToggle) {
        headerContent = (
          <ABaseLink
            onClick={() => {
              this.toggle()
            }}
            cmpExpanded={this.state.open}
            cmpControlsId={cmpId}
          >
            {this.state.open? headerClose : headerOpen}
          </ABaseLink>
        )
    } else if (this.state.open) {
      headerContent = headerClose
    } else {
      headerContent = headerOpen
    }

    customClass += hasBorder ? '' : ' expand-panel-custom-no-border'
    customClass += hasMargin ? '' : ' expand-panel-custom-no-margin'
    customClass += hasPadding ? '' : ' expand-panel-custom-no-padding'

    if (!isEmpty(footer)) {
      footerContent = (
        <div className={'expand-panel-custom__footer'}>{footer}</div>
      )
    }

    return (
      <div className={`expand-panel-custom ${colourClass}${customClass}`}>
        <div className={'expand-panel-custom__toggle'}>
          <div className={'expand-panel-custom__header'}>{headerContent}</div>
        </div>
        <APanel collapsible expanded={this.state.open} id={cmpId}>
          {children}
        </APanel>
        {footerContent}
      </div>
    )
  }

  static propTypes = {
    /** Custom header content when panel is open */
    headerOpen: PropTypes.element,
    /** Custom header content when panel is closed */
    headerClose: PropTypes.element,
    /** Header label when panel is closed */
    headerOpenLabel: PropTypes.string,
    /** Header label when panel is open */
    headerCloseLabel: PropTypes.string,
    /** Header label can be a h1 - h6 tag */
    headerLabelHeaderType: PropTypes.oneOf(HEADER_TYPES),
    /** content of panel */
    children: PropTypes.object.isRequired,
    /** footer for panel */
    footer: PropTypes.element,
    /** whether or not panel should be open when loaded */
    defaultOpen: PropTypes.bool,
    /** font awesome icon class for when panel is closed */
    openIconClass: PropTypes.string,
    /** font awesome icon class for when panel is open */
    closeIconClass: PropTypes.string,
    /** function to run when panel is collpased or expanded */
    onToggle: PropTypes.func,
    /** colour for background and border - currently only supporting blue and white but containers.less can be quickly updated to add more */
    colourTheme: PropTypes.oneOf(COLOUR_THEMES),
    /** whether or not panel should have a border (usually set to false for custom headers) */
    hasBorder: PropTypes.bool,
    /** whether or not panel should have a margin (usually set to false for custom headers) */
    hasMargin: PropTypes.bool,
    /** whether or not panel should have padding (usually set to false for custom headers) */
    hasPadding: PropTypes.bool,
    /** id from aria-controls to panel */
    cmpId: PropTypes.string,
  }
}

export default AExpandPanelCustom
